import React from 'react';
import { useLocation } from 'react-router-dom';
import Seo from './Seo.jsx';
import { STATIC_SEO } from '../data/seo.js';

// ─────────────────────────────────────────────────────────────────────────
// <StaticSeo> — path-keyed wrapper around <Seo>
//
// Why this exists (s70): seventeen tool components were written against the
// legacy `useStaticSeo()` hook in utils/useSeo.js. That hook wrote the head
// imperatively from a useEffect AND resolved its canonical against a
// a hardcoded www origin — the host that
// contradicts the non-www sitemap, canonical, and JSON-LD used everywhere
// else on the site. Those tools had no routes, so the www origin never
// shipped; the moment they were routed it would have.
//
// Rather than fix the hook and keep two head-management systems alive (the
// react-helmet-async APPENDS-not-REPLACES hazard that produced duplicate
// canonicals once already), this component reads the same STATIC_SEO table
// the hook read and renders the canonical <Seo> component. One head system,
// one origin.
//
// Usage: <StaticSeo /> anywhere in a route component's JSX. Path is derived
// from the router; pass `path` explicitly only to override.
// ─────────────────────────────────────────────────────────────────────────

// s80: `schemaType`, `breadcrumbs`, `jsonLd` and `noindex` pass straight
// through to <Seo> so a static route can declare its schema in one line —
// e.g. <StaticSeo schemaType="webApplication" /> on every tool.
export default function StaticSeo({ path: pathOverride, ogType = 'website', schemaType, breadcrumbs, jsonLd, noindex }) {
  const location = useLocation();
  const path = pathOverride || location.pathname;
  const entry = STATIC_SEO[path] || {};

  return (
    <Seo
      title={entry.metaTitle}
      description={entry.metaDescription}
      path={path}
      ogType={ogType}
      schemaType={schemaType}
      breadcrumbs={breadcrumbs}
      jsonLd={jsonLd}
      noindex={noindex}
    />
  );
}
